'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

export default function AboutPage() {
  const [user, setUser] = useState<{ id: string; email: string; name?: string } | null>(null);

  // Check if user is logged in
  useEffect(() => {
    const checkUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      } else {
        setUser(null);
      }
    };
    
    checkUser();
    
    // Listen for custom storage events (for same-window updates)
    const handleStorageChange = (e: Event) => {
      if (('key' in e && (e as StorageEvent).key === 'user') || ('detail' in e && (e as CustomEvent).detail?.key === 'user')) {
        checkUser();
      }
    };
    
    // Listen for storage events from other windows
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom events from same window
    window.addEventListener('userChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userChanged', handleStorageChange);
    };
  }, []);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm mb-4">
          <Link href="/" className="text-white hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-white">About</span>
        </nav>
        
        <h1 className="text-4xl font-bold text-white mb-4">About Grokypedia</h1>
      </div>

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
          <p className="text-gray-300 text-lg">
            Grokypedia is a free, open-source encyclopedia powered by community contributions and artificial intelligence. 
            Our mission is to make knowledge accessible to everyone, built by everyone.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="grok-card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">🌍 Global Knowledge</h3>
            <p className="text-gray-300">
              We believe knowledge should be freely accessible to people worldwide, regardless of their background or location.
            </p>
          </div>
          
          <div className="grok-card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">🤝 Community Driven</h3>
            <p className="text-gray-300">
              Our content is created and maintained by a diverse community of volunteers, experts, and knowledge enthusiasts.
            </p>
          </div>
          
          <div className="grok-card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">🤖 AI Enhanced</h3>
            <p className="text-gray-300">
              We leverage artificial intelligence to improve content quality, provide summaries, and assist with research.
            </p>
          </div>
          
          <div className="grok-card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">📖 Open Source</h3>
            <p className="text-gray-300">
              Our platform is open source, ensuring transparency, community involvement, and continuous improvement.
            </p>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-4">How It Works</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h4 className="font-semibold text-white">Community Contributions</h4>
                <p className="text-gray-300">Volunteers write and edit articles on topics they&apos;re passionate about.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h4 className="font-semibold text-white">Peer Review</h4>
                <p className="text-gray-300">Other community members review and improve the content for accuracy and clarity.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h4 className="font-semibold text-white">AI Enhancement</h4>
                <p className="text-gray-300">AI tools help improve content quality, suggest improvements, and provide summaries.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <h4 className="font-semibold text-white">Global Access</h4>
                <p className="text-gray-300">Knowledge becomes freely available to everyone worldwide.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grok-card p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">🎯</div>
              <h3 className="font-semibold text-white mb-2">Accuracy</h3>
              <p className="text-gray-300 text-sm">We prioritize factual, well-sourced information.</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">⚖️</div>
              <h3 className="font-semibold text-white mb-2">Neutrality</h3>
              <p className="text-gray-300 text-sm">We present information from a balanced, unbiased perspective.</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔄</div>
              <h3 className="font-semibold text-white mb-2">Continuous Improvement</h3>
              <p className="text-gray-300 text-sm">We constantly evolve and enhance our content and platform.</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Join Our Community</h2>
          <p className="text-gray-300 mb-6">
            Help us build the world&apos;s most comprehensive and accessible knowledge base.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              href="/contribute"
              className="grok-btn-primary px-8 py-3 text-lg font-medium"
            >
              Start Contributing
            </Link>
            <Link 
              href="/contact"
              className="grok-btn-secondary px-8 py-3 text-lg font-medium"
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Getting Started Section - Only show if user is not logged in */}
        {!user && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">🚀 Getting Started</h3>
            <div className="space-y-3 text-sm text-gray-300">
              <p>New to contributing? Here&apos;s how to get started:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Create an account or log in</li>
                <li>Read our content guidelines</li>
                <li>Start with small edits</li>
                <li>Join our community discussions</li>
              </ol>
            </div>
            <Link 
              href="/login"
              className="block w-full grok-btn-primary text-center py-2 px-4 mt-4"
            >
              Sign Up / Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
